/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';
import classNames from 'classnames';
import React, { useState } from 'react';
import { TRANSLATIONS } from '../../translations';
import { EnumSummaryProductsListType } from '$/(cms)/_modules/cmsService/types';
import { pushDataLayer } from '$/components/cms/pushDataLayer';
import { FeLink } from '$/components/FeLink';
import AddIcon from '$/components/icons/AddIcon';
import RemoveIcon from '$/components/icons/RemoveIcon';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

export type SummaryItem = {
  id: string | number | null | undefined;
  title: string | null | undefined;
  description?: string | null | undefined;
  anchorId: string;
  visibleMobile?: boolean;
};

export const scrollToSummaryAnchor = (anchorId: string) => {
  const target = document.getElementById(anchorId);
  if (target) {
    target.scrollIntoView({ behavior: 'smooth' });
  }
};

type SummaryProps = {
  title: string;
  collapsible?: boolean;
  collapsedDefault?: boolean;
  listType?: EnumSummaryProductsListType | null;
  items: SummaryItem[];
  index?: number;
};

export const Summary: React.FC<SummaryProps> = ({
  title,
  collapsible = false,
  collapsedDefault = false,
  listType,
  items,
  index,
}) => {
  const [collapsed, setCollapsed] = useState(collapsedDefault);
  const context = useClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);

  const tailwindListType =
    listType === EnumSummaryProductsListType.BulletList
      ? 'list-disc'
      : 'list-decimal';

  const onClickButton = () => {
    if (collapsible) {
      pushDataLayer({
        event: 'navigate',
        component_type: 'accordion',
        component_title: title,
        action: collapsed ? 'expand' : 'collapse',
        index: index,
      });
      setCollapsed(!collapsed);
    }
  };

  return (
    <div>
      <div
        className={`flex w-full items-center justify-between ${!collapsed ? 'pb-spacing/gap/soft-12px' : ''}`}
      >
        {collapsible && (
          <button
            type="button"
            className="cursor-pointer"
            onClick={onClickButton}
          >
            <div className="flex flex-row items-center gap-1">
              {collapsed ? (
                <AddIcon className="size-[--shapes-icon-size-md]" />
              ) : (
                <RemoveIcon className="size-[--shapes-icon-size-md]" />
              )}
              <h3 className="mhg-alpha-typography-bullet-1 underline text-gray-900">
                {collapsed ? title : staticText.hide_summary}
              </h3>
            </div>
          </button>
        )}
      </div>
      {(!collapsible || !collapsed) && (
        <ul
          className={`flex list-outside pl-[22px] md:pl-6 ${tailwindListType} flex-col gap-[--spacing-gap-soft-8px] px-[--spacing-gap-soft-8px] marker:font-semibold marker:text-gray-800`}
        >
          {items.map(({ id, title, description, anchorId, visibleMobile }) => (
            <li
              key={id ?? anchorId}
              className={classNames('hover:underline', {
                'hidden md:block': !visibleMobile,
              })}
            >
              <FeLink
                href={`#${anchorId}`}
                onClick={() => scrollToSummaryAnchor(anchorId)}
                className="mhg-alpha-typography-bullet-1 mhg-alpha-how-it-work-text-link"
              >
                {title}
              </FeLink>
              {description && (
                <span className="mhg-alpha-typography-body-1 text-text/secondary">
                  {`: ${description}`}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
